/**
 * The design tokens, read back from the stylesheet that declares them.
 *
 * **Not a list kept in this file.** A style guide whose contents are typed out
 * by hand goes stale the first time somebody adds a token and does not think to
 * come here -- and the token they added is precisely the one nobody has looked
 * at yet. Reading the declarations means the page cannot be out of date with the
 * stylesheet, only unable to read it, which it says.
 */

/**
 * Where a token belongs on the page.
 *
 * Three families are recognisable from the name; everything else is `palette`,
 * which the page splits again by whether the resolved value can be painted. The
 * split has to happen there rather than here because it is a fact about the
 * value, and the value depends on the theme in force.
 */
export type TokenKind = 'sidebar' | 'radius' | 'font' | 'palette'

export interface Token {
  name: string
  kind: TokenKind
}

export function tokenKind(name: string): TokenKind {
  if (name.startsWith('--font-')) return 'font'
  if (name === '--radius' || name.startsWith('--radius-')) return 'radius'
  if (name.startsWith('--sidebar')) return 'sidebar'
  return 'palette'
}

/**
 * Whether a value can be painted.
 *
 * Decided from the value rather than the name, because the names carry no rule:
 * `--background` and `--ring` are colours, `--radius` is not, and nothing in
 * either spelling says so. A `<div>` with an unpaintable background is a blank
 * square that looks like a bug in the token.
 *
 * A token that resolves to nothing at all -- one declared only under the theme
 * that is not in force -- fails every test here and is listed with its empty
 * value rather than drawn as a transparent swatch, which is the same thing on
 * screen as a colour that matches the page behind it.
 */
export function isPaintable(value: string): boolean {
  const trimmed = value.trim()
  return (
    trimmed.startsWith('oklch(') ||
    trimmed.startsWith('rgb') ||
    trimmed.startsWith('hsl') ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('color(') ||
    trimmed.startsWith('lab(') ||
    trimmed.startsWith('oklab(')
  )
}

/**
 * Every custom property the stylesheets declare on the root.
 *
 * `--color-*` is left out on purpose: those are Tailwind's alias layer, each one
 * pointing at a token below it, so listing them would show every colour twice
 * and neither copy would be the one to change.
 *
 * A stylesheet from another origin throws on `cssRules` rather than returning
 * nothing, so each is tried separately -- one unreadable sheet must not take the
 * rest of the page with it.
 */
export function declaredTokens(sheets: Iterable<CSSStyleSheet>): Token[] {
  const names = new Set<string>()

  for (const sheet of sheets) {
    let rules: CSSRuleList
    try {
      rules = sheet.cssRules
    } catch {
      continue
    }
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSStyleRule)) continue
      if (!rule.selectorText.includes(':root')) continue
      for (const property of Array.from(rule.style)) {
        if (!property.startsWith('--')) continue
        if (property.startsWith('--color-')) continue
        names.add(property)
      }
    }
  }

  return [...names]
    .sort((a, b) => a.localeCompare(b))
    .map((name) => ({ name, kind: tokenKind(name) }))
}

/**
 * What a token resolves to right now.
 *
 * Read from the document rather than from the rule that declared it, because
 * the answer depends on the theme in force: the same name is two different
 * colours, and the whole point of looking is to see which one.
 */
export function resolvedValue(name: string, element: Element): string {
  return getComputedStyle(element).getPropertyValue(name).trim()
}
